import Popper, { HintPlacement } from '@zvuk-b2b/react-uikit/ui/Popper';
import VideoMediafilePoster from 'application/components/VideoMediafileItemPoster';
import { useDetectTextOverflow } from 'application/hooks';
import React, { useCallback, useState } from 'react';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import { TEXTS } from '../../texts';
import styles from './index.module.scss';
import { parseTimeString } from './utils';
import { DEFAULT_IMAGE_DURATION } from '../../consts';
import { getRepeatsCount } from '../../utils';
import ImageDurationControl from './ImageDurationControl';
import MediafileActions from './MediafileActions';
import VideoRepeatControl from './VideoRepeatControl';

interface ItemContentProps {
  title?: string | null;
  duration?: number | null;
  imgLink?: string | null;
  extension?: string | null;
  playDuration?: number | null;
  onSetDuration: (id: string, duration: number) => void;
  id?: string | null;
  onRemoveMediafile: (id: string) => void;
  onCopyMediafile: (id: string) => void;
  isBlockedDurationEdit?: boolean;
}

const ItemContent: React.FC<ItemContentProps> = ({
  title,
  duration,
  imgLink,
  extension,
  playDuration,
  onSetDuration,
  id,
  onRemoveMediafile,
  onCopyMediafile,
  isBlockedDurationEdit = false,
}) => {
  const { isOverflow, textRef } = useDetectTextOverflow('[data-testid="video_media_title"]');

  const { isMobile } = useMedia();

  const videoHelpText = isMobile ? TEXTS.VIDEO_MOBILE_HELP_TEXT : TEXTS.VIDEO_HELP_TEXT;
  const imageHelpText = isMobile ? TEXTS.IMAGE_MOBILE_HELP_TEXT : TEXTS.IMAGE_HELP_TEXT;

  const repeatCount = getRepeatsCount(duration, playDuration);

  const descriptionText = duration ? videoHelpText : imageHelpText;

  const [isValid, setIsValid] = useState(true);

  const onTimeInputBlur = useCallback(
    (durationString: React.FocusEvent<HTMLInputElement, Element>) => {
      if (id) {
        const newPlayDuration = parseTimeString(durationString.target.value).milliseconds;
        setIsValid(Number(newPlayDuration) > 0);
        onSetDuration(id, newPlayDuration);
      }
    },
    [id, setIsValid, onSetDuration]
  );

  const onTimeInputChange = useCallback(
    (durationString: string) => {
      const newPlayDuration = parseTimeString(durationString).milliseconds;
      setIsValid(Number(newPlayDuration) > 0);
    },
    [setIsValid]
  );

  const onCountInputChange = useCallback(
    (durationString: string) => {
      if (!duration) return;

      const newPlayDuration = Number(durationString) * duration;
      setIsValid(Number(newPlayDuration) > 0);
    },
    [duration, setIsValid]
  );

  const onCountINputBlur = useCallback(
    (durationString: React.FocusEvent<HTMLInputElement, Element>) => {
      if (id && duration) {
        const newPlayDuration = Number(durationString.target.value) * duration;
        setIsValid(Number(newPlayDuration) > 0);
        onSetDuration(id, newPlayDuration);
      }
    },
    [id, duration, setIsValid, onSetDuration]
  );

  return (
    <div className={styles.VideoMediafilePreviewItemContent}>
      <div
        className={styles.VideoMediafilePreviewItemContentPosterContainer}
        data-testid={`video_mediafile_poster_${id}`}>
        <VideoMediafilePoster
          title={title}
          link={imgLink}
          duration={duration || playDuration || DEFAULT_IMAGE_DURATION}
          extension={extension}
        />
      </div>

      <div
        ref={textRef}
        className={styles.VideoMediafilePreviewItemContentTextContainer}>
        <Popper
          placement={HintPlacement.PLACEMENT_TOP_START}
          containerClassName={styles.popper}
          isShow={isOverflow}
          offset={[10, 10]}
          triggerNode={
            <span
              data-testid="video_media_title"
              className={styles.VideoMediafilePreviewItemContentTextContainerTitle}>
              {title}
            </span>
          }>
          {title}
        </Popper>
        {!duration && (
          <ImageDurationControl
            playDuration={playDuration}
            isValid={isValid}
            isBlockedDurationEdit={isBlockedDurationEdit}
            onBlur={onTimeInputBlur}
            onChange={onTimeInputChange}
          />
        )}

        {!!duration && (
          <VideoRepeatControl
            repeatCount={repeatCount}
            isValid={isValid}
            isBlockedDurationEdit={isBlockedDurationEdit}
            onBlur={onCountINputBlur}
            onChange={onCountInputChange}
          />
        )}
        <div className={`${styles.description} ${!isValid && styles.error}`}>{descriptionText}</div>
      </div>
      <MediafileActions
        id={id}
        onCopyMediafile={onCopyMediafile}
        onRemoveMediafile={onRemoveMediafile}
      />
    </div>
  );
};

export default ItemContent;
